// Tenant detection from the hostname. The tenant is identified strictly by the
// first label in front of the root domain — never by the URL path.
//
//   manoj.trimedcare.in    → "manoj"
//   trimedcare.in          → null (platform landing page)
//   www.trimedcare.in      → null (reserved label)
//   s4healthcare.localhost:3000 → "s4healthcare" (local dev; *.localhost resolves to 127.0.0.1)
//   localhost:3000         → REACT_APP_DEV_TENANT, or null
//
// Must stay in sync with the backend's TenantResolutionFilter, which resolves
// the same tenant from the Host header.

const ROOT_DOMAIN = (process.env.REACT_APP_ROOT_DOMAIN || 'trimedcare.in').toLowerCase();
const LOCAL_ROOT  = 'localhost';
const RESERVED_SUBDOMAINS = new Set(['www', 'api', 'admin', 'app', 'mail', 'status', 'docs', 'cdn', 'static', 'assets']);

const hostname = window.location.hostname.toLowerCase();

export const isLocalDevHost =
  hostname === LOCAL_ROOT || hostname === '127.0.0.1' || hostname.endsWith(`.${LOCAL_ROOT}`);

// The domain tenant subdomains hang off for the current environment.
const baseDomain = isLocalDevHost ? LOCAL_ROOT : ROOT_DOMAIN;

function extractSubdomain() {
  if (hostname === LOCAL_ROOT || hostname === '127.0.0.1') {
    return (process.env.REACT_APP_DEV_TENANT || '').trim().toLowerCase() || null;
  }

  const suffix = `.${baseDomain}`;
  // Apex domain, or a host we don't serve tenants on (e.g. the raw CloudFront domain).
  if (!hostname.endsWith(suffix)) return null;

  // Nested labels (a.b.trimedcare.in) are returned as-is: they match no tenant,
  // so the app shows "clinic not found" — the backend rejects them too.
  const sub = hostname.slice(0, -suffix.length);
  if (!sub || RESERVED_SUBDOMAINS.has(sub)) return null;
  return sub;
}

// Resolved once at app boot and never re-read.
export const TENANT_SUBDOMAIN = extractSubdomain();

const portSuffix = () => (window.location.port ? `:${window.location.port}` : '');

// Absolute URL of a tenant's app, e.g. https://manoj.trimedcare.in/login
export function tenantUrl(subdomain, path = '/') {
  return `${window.location.protocol}//${subdomain}.${baseDomain}${portSuffix()}${path}`;
}

// Absolute URL of the platform landing page (the root domain).
export function platformUrl(path = '/') {
  return `${window.location.protocol}//${baseDomain}${portSuffix()}${path}`;
}
